import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import axios from "axios";
import { Col, Row, Spin, Steps, Typography } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import CoreDetailsStep, {} from "./CoreDetailsStep";
import AddressStep from "./AddressStep";
import FreightStep from "./FreightStep";
import ReusablePackagingStep from "./ReusablePackagingStep";
import { axiosErrorHandler } from '../../../../../swzutils';
import { AddressTransformer, ConsignmentFreightTransformer, resolveDefaultSenderId, WaypointTransformer } from "../../../../../swzutils/ConsignmentUtil";
import RequireTenantSelection from "../../../components/RequireTenantSelection";
import ConsignmentReusablePackagingResource from "../api/ConsignmentReusablePackagingResource";
import CachedTenantSettingsRepository from "../../../../../CachedTenantSettingsRepository";
import { useAppSelector } from "../../../../../store";
import { loggedInUserSelector, selectedTenantSelector, selectedTenantSettingsSelector } from "../../../../../store/slice/auth";
export const PARAM_CLONE_CONSIGNMENT_ID = 'cloneConsignmentId';
const CreateWizard = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const loggedInUser = useAppSelector(loggedInUserSelector);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    const selectedTenantSettings = useAppSelector(selectedTenantSettingsSelector);
    const coreDetailsStepRef = useRef(null);
    const [currentStepIdx, setCurrentStepIdx] = useState(0);
    const [showSpinner, setShowSpinner] = useState(false);
    const [tenantId, setTenantId] = useState(0);
    const [cloneConsignment, setCloneConsignment] = useState(null);
    const [pickupWaypoint, setPickupWaypoint] = useState(WaypointTransformer.emptyWaypoint());
    const [deliveryWaypoint, setDeliveryWaypoint] = useState(WaypointTransformer.emptyWaypoint());
    const [cloneConsignmentTenantSettings, setCloneConsignmentTenantSettings] = useState(null);
    const [freight, setFreight] = useState([]);
    const [consignmentReusablePackagings, setConsignmentReusablePackagings] = useState([]);
    useEffect(() => {
        if (searchParams.has(PARAM_CLONE_CONSIGNMENT_ID)) {
            loadCloneConsignment(Number(searchParams.get(PARAM_CLONE_CONSIGNMENT_ID)));
        }
    }, [searchParams.get(PARAM_CLONE_CONSIGNMENT_ID)]);
    useEffect(() => {
        if (!searchParams.has(PARAM_CLONE_CONSIGNMENT_ID) && selectedTenant?.id) {
            // If the user changes tenant selection midway through the wizard
            // then go back to step 1 since all entered info is for a different tenant.
            const defaultSenderId = resolveDefaultSenderId(loggedInUser, selectedTenant);
            if (defaultSenderId) {
                setPickupWaypoint({
                    companyName: null,
                    contactEmail: null,
                    contactName: null,
                    contactTelephone: null,
                    sourcedSenderReceiverId: defaultSenderId,
                    address: AddressTransformer.emptyAddressResponse()
                });
            }
            else {
                setPickupWaypoint(WaypointTransformer.emptyWaypoint());
            }
            setCurrentStepIdx(0);
            setDeliveryWaypoint(WaypointTransformer.emptyWaypoint());
            setFreight([]);
            setConsignmentReusablePackagings([]);
            setTenantId(selectedTenant.id);
        }
    }, [loggedInUser, selectedTenant?.id]);
    const loadCloneConsignment = (consignmentId) => {
        axios.get(`/admin/v1/consignments/${consignmentId}`)
            .then(response => {
            let consignment = response.data;
            setCloneConsignment(consignment);
            setPickupWaypoint(WaypointTransformer.extractPickupWaypoint(consignment));
            setDeliveryWaypoint(WaypointTransformer.extractDeliveryWaypoint(consignment));
            setTenantId(consignment.tenantId);
            setFreight(consignment.freight);
            loadConsignmentReusablePackaging(consignment.id);
            loadTenantSettings(consignment.tenantId);
            setCurrentStepIdx(0);
        })
            .catch(axiosErrorHandler);
    };
    function loadTenantSettings(tenantId) {
        CachedTenantSettingsRepository.getSettingsForTenant(tenantId)
            .then(setCloneConsignmentTenantSettings)
            .catch(axiosErrorHandler);
    }
    function loadConsignmentReusablePackaging(consignmentId) {
        ConsignmentReusablePackagingResource.get(consignmentId, setConsignmentReusablePackagings, axiosErrorHandler, () => { });
    }
    const nextStep = () => {
        setCurrentStepIdx(currentStepIdx + 1);
    };
    const previousStep = () => {
        setCurrentStepIdx(currentStepIdx - 1);
    };
    const onPickupWaypointComplete = (updatedWaypoint) => {
        setPickupWaypoint(WaypointTransformer.extractFromConsignmentWaypointUpdateRequest(updatedWaypoint));
        nextStep();
    };
    const onDeliveryWaypointComplete = (updatedWaypoint) => {
        setDeliveryWaypoint(WaypointTransformer.extractFromConsignmentWaypointUpdateRequest(updatedWaypoint));
        nextStep();
    };
    const onCreateConsignment = (postOnMarketplace) => {
        setShowSpinner(true);
        const request = buildConsignmentFromForm(coreDetailsStepRef.current?.getFieldsValue());
        axios.post('/admin/v1/consignments', request)
            .then(response => {
            let url = `/app/consignments/view/${response.data.id}`;
            if (postOnMarketplace) {
                url += '?showPlaceOnMarketModal=true';
            }
            navigate(url);
        })
            .catch(axiosErrorHandler)
            .finally(() => setShowSpinner(false));
    };
    const buildConsignmentFromForm = (formValues) => {
        return {
            tenantId: tenantId,
            customerId: formValues.customerId,
            divisionId: formValues.divisionId,
            customerRef: formValues.customerRef,
            salesOrderRef: formValues.salesOrderRef,
            orderDate: formValues.orderDate?.toISOString(),
            timezoneName: formValues.timezoneName,
            pickupSourcedSenderReceiverId: pickupWaypoint.sourcedSenderReceiverId,
            pickupAddress: AddressTransformer.fromResponse(pickupWaypoint.address),
            pickupCompanyName: pickupWaypoint.companyName,
            pickupContactName: pickupWaypoint.contactName,
            pickupTelephone: pickupWaypoint.contactTelephone,
            pickupContactEmail: pickupWaypoint.contactEmail,
            pickupUhfChannelOffice: pickupWaypoint.uhfChannelOffice,
            pickupUhfChannelLoader: pickupWaypoint.uhfChannelLoader,
            pickupFrom: formValues.pickupDateTimeRange ? formValues.pickupDateTimeRange[0].toISOString() : undefined,
            pickupTo: formValues.pickupDateTimeRange ? formValues.pickupDateTimeRange[1].toISOString() : undefined,
            deliverySourcedSenderReceiverId: deliveryWaypoint.sourcedSenderReceiverId,
            deliveryAddress: AddressTransformer.fromResponse(deliveryWaypoint.address),
            deliveryCompanyName: deliveryWaypoint.companyName,
            deliveryContactName: deliveryWaypoint.contactName,
            deliveryTelephone: deliveryWaypoint.contactTelephone,
            deliveryContactEmail: deliveryWaypoint.contactEmail,
            deliveryUhfChannelOffice: deliveryWaypoint.uhfChannelOffice,
            deliveryUhfChannelLoader: pickupWaypoint.uhfChannelLoader,
            deliveryFrom: formValues.deliveryDateTimeRange[0].toISOString(),
            deliveryTo: formValues.deliveryDateTimeRange[1].toISOString(),
            instructions: formValues.instructions,
            pickUpPhotoRequired: formValues.pickUpPhotoRequired,
            completionPhotoRequired: formValues.completionPhotoRequired,
            completionSignatureRequired: formValues.completionSignatureRequired,
            specialRequirements: formValues.specialRequirements ? formValues.specialRequirements.map((id) => { return { "id": id }; }) : undefined,
            scheduledCommunicationChannels: formValues.scheduledCommunicationChannels,
            startedCommunicationChannels: formValues.startedCommunicationChannels,
            completedCommunicationChannels: formValues.completedCommunicationChannels,
            freight: freight.map(ConsignmentFreightTransformer.requestFromResponse),
            reusablePackagings: consignmentReusablePackagings.map((crp) => {
                return {
                    reusablePackagingId: crp.reusablePackaging.id,
                    scheduledPickupQuantity: crp.scheduledPickupQuantity,
                    scheduledDropQuantity: crp.scheduledDropQuantity,
                    returnToSenderReceiverId: crp.returnToSenderReceiver ? crp.returnToSenderReceiver.id : undefined
                };
            })
        };
    };
    if (!tenantId) {
        return (_jsx(RequireTenantSelection, { children: "\u00A0" }));
    }
    const tenantSettings = searchParams.has(PARAM_CLONE_CONSIGNMENT_ID) ? cloneConsignmentTenantSettings : selectedTenantSettings;
    if (!tenantSettings?.deliveryNotifications) {
        // HACK Alert.  The store initialises tenantSettings to empty object {} so
        // we need to test a field to know if it's actually loaded from the server
        return (_jsxs(Typography.Title, { className: "text-center", children: [_jsx(LoadingOutlined, { spin: true }), " Loading..."] }));
    }
    const stepItems = [
        { title: 'Details', description: 'Delivery Details' },
        { title: 'Pickup', description: 'Pickup Location', disabled: currentStepIdx < 1 },
        { title: 'Delivery', description: 'Delivery Location', disabled: currentStepIdx < 2 },
        { title: 'Freight', description: 'Freight', disabled: currentStepIdx < 3 }
    ];
    if (tenantSettings?.reusablePackagingEnabled) {
        stepItems.push({ title: 'Reusable Packaging', description: 'Reusable Packaging', disabled: currentStepIdx < 4 });
    }
    return (_jsxs(_Fragment, { children: [_jsxs(Row, { gutter: 10, className: "no-margin", children: [" ", _jsx(Col, { xs: 22, offset: 1, children: _jsx(Steps, { current: currentStepIdx, style: { marginBottom: 15 }, items: stepItems }) })] }), _jsxs(Spin, { spinning: showSpinner, children: [_jsx(CoreDetailsStep, { active: currentStepIdx === 0, ref: coreDetailsStepRef, tenantId: tenantId, defaultTimezoneName: selectedTenant?.timezone?.name, cloneConsignment: cloneConsignment, tenantSettings: tenantSettings, onComplete: nextStep }), _jsx(AddressStep, { active: currentStepIdx === 1, tenantId: tenantId, isPickup: true, isCreate: true, waypoint: pickupWaypoint, onBack: previousStep, onComplete: onPickupWaypointComplete }), _jsx(AddressStep, { active: currentStepIdx === 2, tenantId: tenantId, isPickup: false, isCreate: true, waypoint: deliveryWaypoint, onBack: previousStep, onComplete: onDeliveryWaypointComplete }), _jsx(FreightStep, { active: currentStepIdx === 3, isFinalStep: !tenantSettings?.reusablePackagingEnabled, tenantId: tenantId, freight: freight, onChange: setFreight, onBack: previousStep, onNext: nextStep, onSubmit: onCreateConsignment }), tenantSettings?.reusablePackagingEnabled &&
                        _jsx(ReusablePackagingStep, { active: currentStepIdx === 4, tenantId: tenantId, consignmentPackagings: consignmentReusablePackagings, onChange: setConsignmentReusablePackagings, onBack: previousStep, onSubmit: onCreateConsignment })] })] }));
};
export default CreateWizard;
